import React, { useState, useEffect } from 'react';
import {
  Outlet,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import PressReleaseReducer from '../reducers/PressReleaseReducer';
import PromoteSitesReducer from '../reducers/PromoteSitesReducer';
import PressReleaseService from '../services/pressReleaseService';
import {
  pressReleaseContactFields,
  pressReleaseCreateFields,
  requiredField,
} from '../../helpers/pressReleaseConstants';
import { checkoutURL, checkoutBreadcrumbs, checkoutScreens } from '../helpers/divewireConstants';
import Breadcrumbs from '../../../common/components/Breadcrumbs';
import PromoteService from '../services/promoteService';
import getActivePackage from '../../helpers/getActivePackage';
import getActivePackageObject from '../../helpers/getActivePackageObject';
import EventsHelper from '../helpers/eventsHelper';
import useDependentRef from '../../../common/hooks/useDependentRef';
import useCleanupEffectBeforeUnloading from '../../../common/hooks/useCleanupEffectBeforeUnloading';
import getPriceSummaryValues from '../helpers/getPriceSummaryValues';
import PRESS_RELEASE_STATUS from '../helpers/pressReleaseUtils';

const CheckoutScreenContainer = () => {
  // STATE
  const [pressRelease, dispatch] = PressReleaseReducer();
  const [pressReleaseMetadata, setPressReleaseMetadata] = useState({
    cloudinary_cloud_name: '',
    crosspost_packages: [],
    current_site_pk: 0,
    current_site_name: '',
    multimedia_add_on_price: 0,
    press_release_list_price: 0,
    stripe_public_key: '',
    topic_newsletter_add_on_price: 0,
    pressrelease_contact_email: '',
    press_release_faq_url: '',
  });
  const [promoteSites, dispatchSites] = PromoteSitesReducer();
  const [nonFieldError, setNonFieldError] = useState('');
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const currentRoute = location?.pathname.replace(/\/$/, '').split('/').pop();
  const totalPriceInDollars = (
    getPriceSummaryValues(
      pressRelease,
      pressReleaseMetadata,
      promoteSites.sitesList,
    )?.totalPriceInDollars
  );

  const getRequiredFields = (screenFields, excludes = []) => screenFields
    .filter((field) => field.validators.includes(requiredField))
    .filter((field) => !excludes.includes(field.fieldName))
    .map((field) => field.fieldName);

  const isValidNavigation = () => {
    let isValid = true;
    let redirectRoute = null;
    /* eslint-disable default-case, no-fallthrough */
    switch (currentRoute) {
      case 'promote':
      case 'review':
      case 'payment':
        // Exclude accept_content_policy from validation as it is not persisted in db
        if (getRequiredFields(pressReleaseCreateFields, ['accept_content_policy'])
          .some((field) => !pressRelease[field])
        ) {
          isValid = false;
          redirectRoute = 'create';
        }
      // intentionally falling through to validate Contact required fields as well
      case 'create':
        if (getRequiredFields(pressReleaseContactFields).some((field) => !pressRelease[field])) {
          isValid = false;
          redirectRoute = 'contact';
        }
    }
    /* eslint-enable default-case, no-fallthrough */
    return [isValid, redirectRoute];
  };

  // EFFECTS
  useEffect(() => {
    if (!isLoading) {
      const [isValid, redirectRoute] = isValidNavigation();
      if (!isValid) {
        navigate(`${checkoutURL}/${token}/${redirectRoute}`);
      }
    }
  }, [currentRoute, isLoading]);

  // Bootstrap with initial data fetch
  useEffect(() => {
    setIsLoading(true);
    if (token) {
      (async () => {
        try {
          const promoteApi = new PromoteService(token);
          const latestSitesList = await promoteApi.getSitesList();
          dispatchSites({
            type: 'SET_SITES',
            payload: latestSitesList,
          });

          const pressReleaseApi = new PressReleaseService(token);
          const { data } = await pressReleaseApi.getPressRelease();
          setPressReleaseMetadata(data.metadata);
          dispatch({
            type: 'SET_PRESS_RELEASE',
            payload: {
              ...data.press_release,
            },
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      (async () => {
        try {
          const pressReleaseApi = new PressReleaseService();
          const { data } = await pressReleaseApi.getMetaData();
          setPressReleaseMetadata(data.metadata);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [token]);

  // If the press release has been removed/refunded, redirect to the server-rendered removed page
  useEffect(() => {
    if (isLoading) return;
    if (!pressRelease) return;

    const isRemoved = (
      pressRelease.published === false
      && (
        (pressRelease.status === PRESS_RELEASE_STATUS.PAID && !!pressRelease.unpublish_date)
        || [
          PRESS_RELEASE_STATUS.REFUNDED,
          PRESS_RELEASE_STATUS.REFUND_PENDING,
        ].includes(pressRelease.status)
      )
    );

    if (isRemoved) {
      window.location.replace(`/press-release/removed?token=${pressRelease.checkout_token}`);
    }
  }, [isLoading, pressRelease]);

  // [START: Send GA event on component unload]
  // TODO: package this into a custom hook

  // Save/update data sent to GA in browser state via ref so values are accessible after unload
  const browserState = useDependentRef((x) => x, {
    url: window.location.origin + location?.pathname,
    currentRoute,
    totalPriceInDollars,
    prStatus: pressRelease.status,
  });

  // For current browser session, send GA event for page view of the checkout flow if it's
  // the first time the user has viewed the current route. Uses session storage to
  // determine first visit.
  const sendGACheckoutStep = () => {
    const screenCookie = sessionStorage.getItem(browserState.current.currentRoute);
    const pressReleaseStatus = browserState.current.prStatus;
    if (!screenCookie) {
      sessionStorage.setItem(browserState.current.currentRoute, 'true');
      // Only send checkout events when status is not paid or status is paid and we're coming from
      // the payment screen. This effectively prevents sending events when we are in the edit flow.
      if ((pressReleaseStatus !== PRESS_RELEASE_STATUS.PAID) || ((pressReleaseStatus === PRESS_RELEASE_STATUS.PAID) && (sessionStorage.getItem('payment')))) {
        EventsHelper.sendCheckoutStep(
          browserState.current.currentRoute,
          browserState.current.url,
          browserState.current.totalPriceInDollars,
        );
      }
    }
  };

  // Clear session storage so that during next browser session, the GA events
  // will send from the beginning of the checkout flow.
  const clearCheckoutSessionStorage = () => {
    const screens = checkoutScreens.map((scr) => scr.path);
    screens.forEach((screenName) => {
      if (sessionStorage.getItem(screenName)) {
        sessionStorage.removeItem(screenName);
      }
    });
  };

  // Register both functions to run upon unloading the component, which happens
  // when the user switches pages or if they close the browser session.
  useCleanupEffectBeforeUnloading(sendGACheckoutStep, [currentRoute]);
  useCleanupEffectBeforeUnloading(clearCheckoutSessionStorage, []);

  // [END: Send GA event on component unload]

  // Keep press release state in sync with changes to sites list
  useEffect(() => {
    const selectedSiteIds = promoteSites.sitesList.filter((e) => e.selected).map((e) => e.site_id);
    const selectedAddonIds = promoteSites.sitesList
      .filter((site) => site.selected)
      .map((site) => site.topic_newsletter_add_on_options
        .filter((newsletter) => newsletter.selected)
        .map((newsletter) => newsletter.newsletter_schedule_id))
      .flat();
    const activePkg = getActivePackage(
      selectedSiteIds.length,
      pressReleaseMetadata.crosspost_packages,
    );
    const activePkgObject = getActivePackageObject(
      activePkg,
      pressReleaseMetadata.crosspost_packages,
    );
    const pricePerPressRelease = (
      activePkgObject.price_per_press_release
      ?? pressReleaseMetadata.press_release_list_price
    );
    dispatch({
      type: 'SET_PRESS_RELEASE',
      payload: {
        crosspost_sites: selectedSiteIds,
        topic_newsletter_add_ons: selectedAddonIds,
        price_per_press_release: pricePerPressRelease,
      },
    });
  }, [promoteSites.sitesList, pressReleaseMetadata]);

  // delay loading contents until bootstrapping is complete
  if (isLoading) {
    return null;
  }

  return (
    <>
      <div className="row columns no-print breadcrumb-wrapper">
        {(currentRoute !== 'confirmation' && pressRelease?.status !== PRESS_RELEASE_STATUS.PAID) && (
          <Breadcrumbs
            ariaLabel="Press release creation breadcrumb"
            baseUrl={checkoutURL}
            checkoutToken={token}
            screen={currentRoute}
            screens={checkoutBreadcrumbs}
          />
        )}
      </div>
      <Outlet
        context={{
          pressRelease,
          dispatch,
          pressReleaseMetadata,
          promoteSites,
          dispatchSites,
          nonFieldError,
          setNonFieldError,
        }}
      />
    </>
  );
};

export default CheckoutScreenContainer;
